<template>
  <section :class="['notes-panel', { 'notes-panel--compact': compact }]">
    <div class="notes-panel__header">
      <div>
        <p class="notes-panel__eyebrow">Video</p>
        <h3 class="notes-panel__title">Notas del video</h3>
      </div>
      <v-chip small class="notes-panel__chip">
        {{ totalNotes }}
      </v-chip>
    </div>

    <div v-if="allowCreate" class="notes-panel__capture">
      <div class="notes-panel__time">
        <v-icon size="18">mdi-clock-outline</v-icon>
        <span>{{ formattedCurrentTime }}</span>
      </div>
      <v-btn
        small
        text
        color="#0076ff"
        class="notes-panel__refresh"
        @click="$emit('capture-time')"
      >
        <v-icon left size="16">mdi-crosshairs-gps</v-icon>
        Usar tiempo actual
      </v-btn>
    </div>

    <v-textarea
      v-if="allowCreate"
      v-model="noteModel"
      outlined
      dense
      auto-grow
      rows="2"
      label="Escribe una nota para este momento"
      hide-details="auto"
      class="notes-panel__textarea"
    />

    <div v-if="allowCreate" class="notes-panel__actions">
      <v-btn
        rounded
        depressed
        color="#0076ff"
        dark
        :disabled="loading || !noteModel.trim()"
        :loading="loading"
        @click="$emit('submit')"
      >
        <v-icon left>mdi-note-plus-outline</v-icon>
        Agregar nota
      </v-btn>
    </div>

    <div v-if="loading && notes.length === 0" class="notes-panel__empty">
      <v-progress-circular indeterminate size="26" width="2" color="#0076ff" />
      <span>Cargando notas...</span>
    </div>

    <div v-else-if="notes.length === 0" class="notes-panel__empty">
      <v-icon color="#8a98a5" size="28">mdi-note-text-outline</v-icon>
      <span>Aun no hay notas para este video</span>
    </div>

    <div v-else class="notes-panel__list">
      <article
        v-for="note in paginatedNotes"
        :key="note.id"
        class="notes-panel__item"
      >
        <button
          type="button"
          class="notes-panel__timestamp"
          @click="$emit('seek', note)"
        >
          {{ formatTime(note.time_seconds) }}
        </button>
        <p>{{ noteBody(note) }}</p>
      </article>
    </div>

    <div v-if="showPagination" class="notes-panel__pagination">
      <span class="notes-panel__pagination-meta">
        {{ paginationLabel }}
      </span>
      <v-pagination
        :value="currentPage"
        :length="totalPages"
        total-visible="5"
        color="#0076ff"
        @input="changePage"
      />
    </div>
  </section>
</template>

<script>
export default {
  name: 'ContentNotes',
  data() {
    return {
      localCurrentPage: 1,
      notesPerPage: 4,
    };
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    notes: {
      type: Array,
      default: () => [],
    },
    currentTime: {
      type: Number,
      default: 0,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    noteModel: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    formattedCurrentTime() {
      return this.formatTime(this.currentTime);
    },
    hasBackendPagination() {
      return Boolean(this.pagination && this.pagination.last_page);
    },
    totalNotes() {
      return Number(this.pagination.total || this.notes.length);
    },
    currentPage() {
      return Number(this.pagination.current_page || this.localCurrentPage);
    },
    totalPages() {
      if (this.hasBackendPagination) {
        return Math.max(1, Number(this.pagination.last_page || 1));
      }

      return Math.max(1, Math.ceil(this.notes.length / this.notesPerPage));
    },
    showPagination() {
      return this.totalPages > 1;
    },
    paginatedNotes() {
      if (this.hasBackendPagination) return this.notes;

      const start = (this.localCurrentPage - 1) * this.notesPerPage;
      return this.notes.slice(start, start + this.notesPerPage);
    },
    paginationLabel() {
      if (this.totalNotes === 0) return '0 notas';

      if (this.hasBackendPagination) {
        const from = Number(this.pagination.from || 0);
        const to = Number(this.pagination.to || this.notes.length);
        return `${from}-${to} de ${this.totalNotes}`;
      }

      const start = (this.localCurrentPage - 1) * this.notesPerPage + 1;
      const end = Math.min(this.localCurrentPage * this.notesPerPage, this.notes.length);
      return `${start}-${end} de ${this.notes.length}`;
    },
  },
  watch: {
    notes() {
      if (!this.hasBackendPagination && this.localCurrentPage > this.totalPages) {
        this.localCurrentPage = this.totalPages;
      }
    },
  },
  methods: {
    changePage(page) {
      if (this.hasBackendPagination) {
        this.$emit('page-change', page);
        return;
      }

      this.localCurrentPage = page;
    },
    noteBody(note) {
      return note.note || note.body || '';
    },
    formatTime(seconds) {
      const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
      const minutes = Math.floor(totalSeconds / 60);
      const remainingSeconds = totalSeconds % 60;

      return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped>
.notes-panel {
  width: 100%;
  background: #fff;
  border: 1px solid #eef2f6;
  border-radius: 8px;
  padding: 20px;
}

.notes-panel--compact {
  border: 0;
  border-radius: 8px;
}

.notes-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.notes-panel__eyebrow {
  margin: 0 0 2px;
  color: #8a98a5;
  font: normal normal 400 12px/18px Poppins;
}

.notes-panel__title {
  margin: 0;
  color: #101820;
  font: normal normal 700 18px/26px Poppins;
}

.notes-panel__chip {
  background: #f6f8fa !important;
  color: #607080;
}

.notes-panel__capture {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  margin-bottom: 14px;
  background: #f8fafc;
  border: 1px solid #edf1f5;
  border-radius: 8px;
}

.notes-panel__time {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #101820;
  font: normal normal 700 14px/20px Poppins;
}

.notes-panel__time .v-icon {
  color: #0076ff;
}

.notes-panel__refresh {
  text-transform: none;
  font: normal normal 600 12px/18px Poppins;
}

.notes-panel__textarea {
  margin-top: 6px;
}

.notes-panel__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.notes-panel__actions .v-btn {
  text-transform: none;
  font: normal normal 600 13px/20px Poppins;
}

.notes-panel__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 84px;
  padding: 18px;
  margin-top: 16px;
  color: #6f7d8a;
  background: #f8fafc;
  border-radius: 8px;
  font: normal normal 400 13px/20px Poppins;
  text-align: center;
}

.notes-panel__list {
  display: grid;
  gap: 10px;
  margin-top: 16px;
}

.notes-panel__item {
  display: grid;
  gap: 8px;
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #edf1f5;
  border-radius: 8px;
}

.notes-panel__timestamp {
  width: fit-content;
  padding: 4px 10px;
  border: 0;
  border-radius: 999px;
  background: #e9f7fb;
  color: #0076ff;
  cursor: pointer;
  font: normal normal 700 12px/18px Poppins;
}

.notes-panel__item p {
  margin: 0;
  color: #26323d;
  font: normal normal 400 14px/22px Poppins;
  word-break: break-word;
}

@media (max-width: 640px) {
  .notes-panel,
  .notes-panel--compact {
    padding: 18px 16px 16px;
    border: 0;
    border-radius: 18px 18px 0 0;
    box-shadow: 0 -14px 34px rgba(16, 24, 32, 0.18);
  }

  .notes-panel__header {
    align-items: center;
    margin-bottom: 14px;
  }

  .notes-panel__eyebrow {
    display: none;
  }

  .notes-panel__title {
    font-size: 17px;
    line-height: 24px;
  }

  .notes-panel__chip {
    min-width: 28px;
    height: 24px;
    justify-content: center;
  }

  .notes-panel__capture {
    align-items: center;
    flex-direction: row;
    gap: 8px;
    padding: 10px;
    margin-bottom: 12px;
    border-color: #d7e9ff;
    background: #f5faff;
  }

  .notes-panel__time {
    flex: 0 0 auto;
    font-size: 13px;
    line-height: 18px;
  }

  .notes-panel__refresh {
    min-width: 0 !important;
    padding: 0 6px !important;
    font-size: 11px;
    line-height: 16px;
  }

  .notes-panel__textarea {
    margin-top: 0;
  }

  .notes-panel__textarea >>> .v-input__slot {
    min-height: 88px !important;
    border-radius: 8px;
  }

  .notes-panel__textarea >>> textarea {
    font-size: 13px;
    line-height: 20px;
  }

  .notes-panel__actions {
    justify-content: stretch;
    margin-top: 12px;
  }

  .notes-panel__actions .v-btn {
    width: 100%;
    min-height: 42px;
  }

  .notes-panel__empty {
    flex-direction: column;
    min-height: 118px;
    margin-top: 14px;
    padding: 22px 18px;
    font-size: 13px;
    line-height: 20px;
  }

  .notes-panel__list {
    margin-top: 14px;
  }

  .notes-panel__item {
    padding: 12px;
  }

  .notes-panel__item p {
    font-size: 13px;
    line-height: 20px;
  }

  .notes-panel__pagination {
    justify-content: center;
  }

  .notes-panel__pagination-meta {
    width: 100%;
    text-align: center;
  }
}
</style>
